import classNames from "classnames";
import React, { useCallback, useState } from "react";
import { Dropdown, DropdownBody, DropdownBodyProps, DropdownHover, DropdownHoverProps } from "../../layout";
import { LazyLoadImage, Link, LinkButton } from "../../misc";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import "./DestinationDropdown.scss";
import { parseColourString } from "../..";

interface DestinationDropdownProps {

  /**
   * The regions to display in the dropdown
   */
  regions?: ILocationSummary[];

  /**
   * The countries to display in the dropdown. Their parent regions must be in the list of regions.
   */
  countries?: ILocationSummary[];
}

const DestinationHoverLink: React.FC<DropdownHoverProps> = (props) => {
  return (
    <DropdownHover {...props} mouseOnly>
      <LinkButton
        to={WebRoutes.destinations()}
        buttonClass="secondary-button"
        className="heading--4 is-marginless is-paddingless"
      >
        Destinations
      </LinkButton>
    </DropdownHover>
  );
};

type Props = DropdownBodyProps & DestinationDropdownProps;

const DestinationDropdownBody: React.FC<Props> = ({ regions, countries, ...props }) => {
  const [activeRegion, setActiveRegion] = useState<ILocationSummary | undefined>(regions?.[0]);
  const imageUrl = activeRegion?.heroImage?.largeUrl ?? "/images/missing-image.jpg";

  return (
    <DropdownBody {...props} className="destination-dropdown__body">
      <div className="columns is-marginless">
        <div className={"column is-hidden-tablet-only is-hidden-desktop-only "
          + "is-marginless is-paddingless destination-dropdown__image"}>
          <LazyLoadImage src={imageUrl} alt="" />
        </div>

        <div className="column is-paddingless is-4 destination-dropdown__regions">
          {regions == null && (
            <p>Sorry, something went wrong. Please try reloading the page.</p>
          )}

          <ul className="is-marginless destination-dropdown__links">
            {regions?.map(region => {
              if (region.locationStatus !== LocationStatus.Live) {
                return null;
              }

              const setRegion = () => setActiveRegion(region);

              return (
                <li key={region.id}>
                  <label
                    className={classNames("has-text-right", "destination-dropdown__link", {
                      "destination-dropdown__link--focus": activeRegion?.id === region.id
                    })}
                    onClick={setRegion}
                    onMouseOver={setRegion}
                  >
                    <input
                      type="radio"
                      name="region-select"
                      className="u-visually-hidden"
                      onFocus={setRegion}
                      tabIndex={props.isVisible ? 0 : -1}
                    />
                    {region.name}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <nav className="column is-paddingless destination-dropdown__countries">
          <ul className="is-marginless destination-dropdown__links">
            {countries?.map(country => {
              if (country.locationStatus !== LocationStatus.Live) {
                return null;
              }

              const isVisible = props.isVisible && country.parent.id === activeRegion?.id;
              return (
                <li
                  key={country.id}
                  className={classNames({ "u-visually-hidden": !isVisible })}
                  style={{
                    ["--page-accent"]: parseColourString(country.heroImage?.colour)
                  } as React.CSSProperties}
                >
                  <LinkButton
                    buttonClass="secondary-button"
                    className="destination-dropdown__link is-marginless"
                    to={WebRoutes.location(country)}
                    tabIndex={isVisible ? 0 : -1}
                    title={`See more about ${country.name}` + (country.localName ? ` (${country.localName})` : "")}
                  >
                    {country.name}
                  </LinkButton>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </DropdownBody>
  );
}

/**
 * Destinations dropdown on the marketing site.
 * 
 * @status development
 */
export const DestinationDropdown: React.FC<DestinationDropdownProps> = (props) => {
  const renderBody = useCallback(
    bodyProps => <DestinationDropdownBody {...bodyProps} {...props} />,
    [props.countries, props.regions]
  );

  return (
    <Dropdown
      className="destination-dropdown"
      Hover={DestinationHoverLink}
      Body={renderBody}
    />
  );
}
